// @flow

import React, { type Node } from 'react';

import { Empty, Separator, Link } from '@tectonics/look-and-feel/volcanes/atoms';
import { useCurrentAllFeatures } from '@tectonics/prs/payment-spec';
import { IfFeature } from '@tectonics/prs/feature';
import { useUserInputValue } from '@tectonics/prs/user-input-values';
import { InformationBox } from '../../../organisms/information-box';
import mapMarkerIconSrc from './payment-methods/cash/map-marker-icon.svg';
import pagosNetIconSrc from './payment-methods/pagosnet/pagosnet-icon.svg';
import infoIconSrc from './icons/info-icon.svg';
import lightBulbIconSrc from './icons/light-bulb-icon.svg';

import {
  MulticajaInfo,
  WebpayInfo,
  SencillitoInfo,
  ServipagInfo,
  PagoefectivoInfo,
  PixInfo,
  SafetypayCashInfo,
  SafetypayOnlineInfo,
  ColombianExternalPayProviderInfo,
  NupayInfo,
  MpcreditoInfo,
} from './payment-methods';

export function PaymentMethodInfo(): Node {
  const [selectedPaymentMethod] = useUserInputValue('selectedPaymentMethod');

  switch (selectedPaymentMethod) {
    case 'cash':
    case 'eft':
    case 'ewallet':
    case 'boleto':
    case 'banktransfer':
    case 'externalpayprovider':
    case 'creditcard':
    case 'debitcard':
      // It should be used for ALL payment methods
      // The decision about what should be displayed have to use FEATURES SPEC
      return <PaymentMethodInfoContent />;
    case 'multicaja':
      return <MulticajaInfo />;

    case 'webpay':
      return <WebpayInfo />;

    case 'sencillito':
      return <SencillitoInfo />;

    case 'servipag':
      return <ServipagInfo />;

    case 'pagoefectivo':
      return <PagoefectivoInfo />;

    case 'pix':
      return <PixInfo />;

    case 'safetypay-cash':
      return <SafetypayCashInfo />;

    case 'safetypay-online':
      return <SafetypayOnlineInfo />;

    case 'nupay':
      return <NupayInfo />;

    case 'mpcredito':
      return <MpcreditoInfo />;

    default:
      return <Empty />;
  }
}

function PaymentMethodInfoContent(): Node {
  const allFeatures = useCurrentAllFeatures();

  return (
    <>

      <IfFeature allFeatures={allFeatures} wanted="display_paynet_payment_locations">
        <InformationBox icon={mapMarkerIconSrc} iconAlt="map marker icon">
          Usted podrá pagar este cupón en efectivo en cualquer sucursal afiliada de PayNet:&nbsp;
          <strong>
            Farmacias Benavides, 7-Eleven, Walmart, Farmacias del Ahorro, Sam’s, Superama,
            Bodega Aurrera, Circle K, Extra, El Asturiano, Waldo’s, Alsuper, Kiosko o Farmacias Guadalajara.
          </strong>
        </InformationBox>
      </IfFeature>

      <IfFeature allFeatures={allFeatures} wanted="display_pagosnet_instructions">
        <InformationBox icon={pagosNetIconSrc} iconAlt="pagosnet icon">
          Al generar tu número de recaudación tendrás 72 horas para finalizar tu pago.
        </InformationBox>
      </IfFeature>

      <IfFeature allFeatures={allFeatures} wanted="display_eft_instructions">
        <InformationBox>
          En caso de no poder realizar tu pago con el método que elegiste, tendrás siempre la opción de generar un nuevo pedido.
          <Separator size={3} direction="vertical"/>
          <Link href="https://descubre.pse.com.co/usuario-pagador/">
            ¿Cómo funciona PSE?
          </Link>
        </InformationBox>
      </IfFeature>

      <IfFeature allFeatures={allFeatures} wanted="display_machpay_instructions">
        <InformationBox icon={infoIconSrc} iconAlt="info icon" fillBackground={true}>
          El QR generado debe ser pago con la app MACH. La confirmación se realizará instantes después de completar el pago.
        </InformationBox>
      </IfFeature>

      <IfFeature allFeatures={allFeatures} wanted="display_boleto_instructions">
        <InformationBox icon={infoIconSrc} iconAlt="info icon" fillBackground={true}>
          Clique no botão abaixo para gerar o boleto, que poderá ser pago em lotéricas,
          em bancos ou no app do seu banco. Após pago, a aprovação do boleto
          acontece em até 2 dias úteis.
        </InformationBox>
      </IfFeature>

      <IfFeature allFeatures={allFeatures} wanted="display_bank_transfer_instructions">
        <InformationBox icon={infoIconSrc} iconAlt="info icon" fillBackground={true}>
          Clique no botão abaixo para gerar as informações de pagamento. A
          transferência poderá ser feita a partir do aplicativo do seu banco, internet
          banking ou caixa eletrônico em uma agência bancária.
        </InformationBox>
      </IfFeature>

      <IfFeature allFeatures={allFeatures} wanted="display_external_pay_provider_instructions">
        <InformationBox icon={lightBulbIconSrc} iconAlt="info icon" fillBackground={true} separatorSize={3.2}>
          You will be redirected to Ozow&apos;s checkout.
          <ul style={{listStylePosition: 'inside', marginLeft: '6px'}}>
            <li>Choose your bank;</li>
            <li>Authenticate with your credentials;</li>
            <li>Complete the payment.</li>
          </ul>
        </InformationBox>
      </IfFeature>

      <IfFeature allFeatures={allFeatures} wanted="display_ecuador_cards_instructions">
        <InformationBox icon={lightBulbIconSrc} iconAlt="info icon" fillBackground={true} separatorSize={3.2}>
          El pago final puede variar en función de cargos adicionales y ajustes realizados durante el proceso de compra.
        </InformationBox>
      </IfFeature>

      <IfFeature allFeatures={allFeatures} wanted="display_colombian_externalpayprovider_instructions">
        <ColombianExternalPayProviderInfo />
      </IfFeature>

      <IfFeature allFeatures={allFeatures} wanted="display_aplazo_externalpayprovider_instructions">
        <InformationBox icon={lightBulbIconSrc} iconAlt="info icon" fillBackground={true} separatorSize={3.2}>
        Al seleccionar el método de pago Aplazo, serás redirigido a la página del banco para completar la transacción de forma segura.
        </InformationBox>
      </IfFeature>
    </>
  );
}
